import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from '../object';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { VolumeGridLike } from '../navigation-mesh';

const FLOATS_PER_VERTEX = 7; // x, y, z, r, g, b, a
const MAX_DEPTH = 12;

export class VisualQuadTreeGrid implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    order: 6000,
    culling: 'none',
    static: true,
    modes: [RenderMode.Overlay],
  };
  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  private vao: WebGLVertexArrayObject;
  private glBuffer: WebGLBuffer;
  private vertexCount = 0;

  // Separate buffer for lines (wireframe boundaries)
  private lineVao: WebGLVertexArrayObject;
  private lineBuffer: WebGLBuffer;
  private lineVertexCount = 0;

  constructor(private ctx: Context) {
    const gl = ctx.gl;

    // Triangle buffer (flat leaf surfaces + complex solid boxes)
    this.glBuffer = gl.createBuffer()!;
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
    const stride = FLOATS_PER_VERTEX * Float32Array.BYTES_PER_ELEMENT;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, stride, 3 * Float32Array.BYTES_PER_ELEMENT);
    gl.bindVertexArray(null);

    // Line buffer (wireframe boundaries)
    this.lineBuffer = gl.createBuffer()!;
    this.lineVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.lineVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, stride, 3 * Float32Array.BYTES_PER_ELEMENT);
    gl.bindVertexArray(null);
  }

  public setVolumeGrid(grid: VolumeGridLike): void {
    const triGeo: number[] = [];
    const lineGeo: number[] = [];

    // TODO: Update debug visualization for ArrayBuffer-backed VolumeGrid.
    // The forEachLeaf callback now provides (nodeType, nodeOffset) instead of
    // JS objects. Reading leaf data inline from the buffer is needed here.
    grid.forEachLeaf((wx, wz, ww, wd, nodeType, nodeOffset, depth) => {
      const [r, g, b] = depthColor(depth);
      const y = 0.1;
      emitLine(lineGeo, wx, y, wz, wx + ww, y, wz, r, g, b, 1.0);
      emitLine(lineGeo, wx + ww, y, wz, wx + ww, y, wz + wd, r, g, b, 1.0);
      emitLine(lineGeo, wx + ww, y, wz + wd, wx, y, wz + wd, r, g, b, 1.0);
      emitLine(lineGeo, wx, y, wz + wd, wx, y, wz, r, g, b, 1.0);
    });

    const gl = this.ctx.gl;

    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(triGeo), gl.STATIC_DRAW);
    this.vertexCount = triGeo.length / FLOATS_PER_VERTEX;

    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(lineGeo), gl.STATIC_DRAW);
    this.lineVertexCount = lineGeo.length / FLOATS_PER_VERTEX;
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings): void {
    if (this.vertexCount === 0 && this.lineVertexCount === 0) return;

    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getTestGeometryShader();
    ctx.setShader(shader);

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.CULL_FACE);

    shader.uniformsContainer
      .setModelViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix);

    // Draw filled triangles
    if (this.vertexCount > 0) {
      gl.bindVertexArray(this.vao);
      gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);
    }

    // Draw wireframe boundaries
    if (this.lineVertexCount > 0) {
      gl.bindVertexArray(this.lineVao);
      gl.drawArrays(gl.LINES, 0, this.lineVertexCount);
    }

    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
    gl.bindVertexArray(null);
  }
}

/** Map tree depth to a color: green (shallow) → yellow → red (deep) */
function depthColor(depth: number): [number, number, number] {
  const t = Math.min(depth / MAX_DEPTH, 1.0);
  // HSL hue from 120° (green) → 0° (red)
  const hue = (1 - t) * 120;
  return hslToRgb(hue / 360, 0.8, 0.5);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  let r: number, g: number, b: number;
  const sector = Math.floor(h * 6);
  switch (sector % 6) {
    case 0:
      r = c;
      g = x;
      b = 0;
      break;
    case 1:
      r = x;
      g = c;
      b = 0;
      break;
    case 2:
      r = 0;
      g = c;
      b = x;
      break;
    case 3:
      r = 0;
      g = x;
      b = c;
      break;
    case 4:
      r = x;
      g = 0;
      b = c;
      break;
    default:
      r = c;
      g = 0;
      b = x;
      break;
  }
  return [r + m, g + m, b + m];
}

function vertex(
  geo: number[],
  x: number,
  y: number,
  z: number,
  r: number,
  g: number,
  b: number,
  a: number,
) {
  geo.push(x, y, z, r, g, b, a);
}

function emitLine(
  geo: number[],
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  r: number,
  g: number,
  b: number,
  a: number,
) {
  vertex(geo, x0, y0, z0, r, g, b, a);
  vertex(geo, x1, y1, z1, r, g, b, a);
}

function emitBox(
  geo: number[],
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  r: number,
  g: number,
  b: number,
  a: number,
) {
  // 6 faces × 2 triangles × 3 vertices = 36 vertices
  // Bottom (Y = y0)
  vertex(geo, x0, y0, z0, r, g, b, a);
  vertex(geo, x1, y0, z0, r, g, b, a);
  vertex(geo, x0, y0, z1, r, g, b, a);
  vertex(geo, x1, y0, z0, r, g, b, a);
  vertex(geo, x1, y0, z1, r, g, b, a);
  vertex(geo, x0, y0, z1, r, g, b, a);
  // Top (Y = y1)
  vertex(geo, x0, y1, z0, r, g, b, a);
  vertex(geo, x0, y1, z1, r, g, b, a);
  vertex(geo, x1, y1, z0, r, g, b, a);
  vertex(geo, x1, y1, z0, r, g, b, a);
  vertex(geo, x0, y1, z1, r, g, b, a);
  vertex(geo, x1, y1, z1, r, g, b, a);
  // Front (Z = z0)
  vertex(geo, x0, y0, z0, r, g, b, a);
  vertex(geo, x0, y1, z0, r, g, b, a);
  vertex(geo, x1, y0, z0, r, g, b, a);
  vertex(geo, x1, y0, z0, r, g, b, a);
  vertex(geo, x0, y1, z0, r, g, b, a);
  vertex(geo, x1, y1, z0, r, g, b, a);
  // Back (Z = z1)
  vertex(geo, x0, y0, z1, r, g, b, a);
  vertex(geo, x1, y0, z1, r, g, b, a);
  vertex(geo, x0, y1, z1, r, g, b, a);
  vertex(geo, x1, y0, z1, r, g, b, a);
  vertex(geo, x1, y1, z1, r, g, b, a);
  vertex(geo, x0, y1, z1, r, g, b, a);
  // Left (X = x0)
  vertex(geo, x0, y0, z0, r, g, b, a);
  vertex(geo, x0, y0, z1, r, g, b, a);
  vertex(geo, x0, y1, z0, r, g, b, a);
  vertex(geo, x0, y0, z1, r, g, b, a);
  vertex(geo, x0, y1, z1, r, g, b, a);
  vertex(geo, x0, y1, z0, r, g, b, a);
  // Right (X = x1)
  vertex(geo, x1, y0, z0, r, g, b, a);
  vertex(geo, x1, y1, z0, r, g, b, a);
  vertex(geo, x1, y0, z1, r, g, b, a);
  vertex(geo, x1, y0, z1, r, g, b, a);
  vertex(geo, x1, y1, z0, r, g, b, a);
  vertex(geo, x1, y1, z1, r, g, b, a);
}
